import {pluralize} from 'BenchlingUtil';
import {ACTION_UNDO_PROCESS_TASK_EXECUTION} from 'notebook/store/VersioningConstants';
import {undoProcessTaskExecution} from 'notebook/structured-tables/ActionFunctionDAO';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  AxisContextMenuActionFunctionParameters,
  StructuredTableContextMenuItem,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import getAllStructuredTablesInNote from 'notebook/structured-tables/util/getAllStructuredTablesInNote';
import rowHasUnsubmittedChanges from 'notebook/structured-tables/util/rowHasUnsubmittedChanges';
import {SelectionRange, TableDimension} from 'notebook/tables/TableConstants';
import getDimensionalOperationText from 'notebook/tables/util/getDimensionalOperationText';
import {Note} from 'notebook/util/NoteUtilConstants';
import {
  ActionResultType,
  performServerSideTableWriteActionWithAudits,
} from 'notebook/util/PerformActionFunctions';
import {DefaultStatusDisplayName} from 'processes/ProcessConstants';
import APIUtil from 'util/APIUtil';

import ProcessOutputStructuredTableAdapter, {
  ProcessOutputStructuredTable,
} from '../ProcessOutputStructuredTableAdapter';
import ProcessTaskStructuredTableAdapter, {
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata,
} from '../ProcessTaskStructuredTableAdapter';

function getRowsInRange<T>(rows: Array<T>, selectionRange: SelectionRange): Array<T> {
  return rows.slice(selectionRange.minIndex, selectionRange.maxIndex + 1);
}

function tasksHaveOutputsInEntry(note: Note, taskIds: Set<string>): boolean {
  const outputTable = getAllStructuredTablesInNote(note).find(
    (structuredTable) => structuredTable.kind === StructuredTableKind.PROCESS_OUTPUT
  ) as ProcessOutputStructuredTable;
  if (outputTable == null) {
    return false;
  }
  const processOutputStructuredTableAdapter = getStructuredTableAdapters()[
    outputTable.kind
  ] as ProcessOutputStructuredTableAdapter;
  return outputTable.rows.some((row) => {
    const outputId = processOutputStructuredTableAdapter.getOutputId(outputTable.columns, row);
    const taskId = processOutputStructuredTableAdapter.getTaskId(outputTable.columns, row);
    // outputId may be the empty string if the cell is empty
    return outputId && taskId != null && taskIds.has(taskId);
  });
}

function shouldShowUndoExecutionActionFunction({
  entryOrTemplateId,
  note,
  structuredTableAdapter,
  structuredTable,
  computedTable,
  selectionRange,
  locked,
}: AxisContextMenuActionFunctionParameters<
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata
> & {structuredTableAdapter: ProcessTaskStructuredTableAdapter}): boolean {
  if (
    selectionRange == null ||
    locked ||
    entryOrTemplateId == null ||
    note == null ||
    !APIUtil.isBaseEntry(entryOrTemplateId)
  ) {
    return false;
  }

  const selectedRows = getRowsInRange(structuredTable.rows, selectionRange);
  const selectedComputedRows = getRowsInRange(computedTable, selectionRange);

  if (
    selectedRows.some((row, idx) =>
      rowHasUnsubmittedChanges(
        selectedComputedRows[idx],
        row,
        structuredTable.columns,
        structuredTable.schemas
      )
    )
  ) {
    return false;
  }

  const taskIds = structuredTableAdapter.getTaskIdsFromRows(
    structuredTable.columns,
    selectedRows,
    selectedComputedRows
  );
  if (selectedRows.length !== taskIds.length) {
    return false;
  }

  if (
    selectedRows.some((row) => {
      const status = structuredTableAdapter.getStatus(structuredTable.columns, row);
      return status == null || status.displayName !== DefaultStatusDisplayName.IN_PROGRESS;
    })
  ) {
    return false;
  }

  if (tasksHaveOutputsInEntry(note, new Set(taskIds))) {
    return false;
  }

  return true;
}

export function getUndoExecutionActionFunction(
  params: AxisContextMenuActionFunctionParameters<
    ProcessTaskStructuredTableMetadata,
    ProcessTaskStructuredTableRowMetadata
  >
): StructuredTableContextMenuItem | null {
  const processTaskStructuredTableAdapter =
    params.structuredTableAdapter as ProcessTaskStructuredTableAdapter;
  if (
    !shouldShowUndoExecutionActionFunction({
      ...params,
      structuredTableAdapter: processTaskStructuredTableAdapter,
    })
  ) {
    return null;
  }
  const taskIds = processTaskStructuredTableAdapter.getTaskIdsFromRows(
    params.structuredTable.columns,
    getRowsInRange(params.structuredTable.rows, params.selectionRange!),
    getRowsInRange(params.computedTable, params.selectionRange!)
  );
  return {
    name: getDimensionalOperationText({
      verb: `Undo execution of ${pluralize(taskIds.length, 'task')} from`,
      selectionRange: params.selectionRange,
      dimension: TableDimension.ROW,
    }),
    onSelect: () => {
      performServerSideTableWriteActionWithAudits({
        entryId: params.entryOrTemplateId!,
        loadingScreenMessage: 'Undoing task execution...',
        autoSaverAction: ACTION_UNDO_PROCESS_TASK_EXECUTION,
        doPerformAction: async (_?, __?, dataModsHeader?) => {
          await undoProcessTaskExecution(
            params.entryOrTemplateId!,
            params.structuredTable,
            params.computedTable,
            params.actionFunctionContext,
            taskIds,
            dataModsHeader
          );
          return {resultType: ActionResultType.SUCCESS, responseData: null};
        },
        analyticsActionForPerfTracking: 'undo_execution_process_task_structured_table_rows',
        structuredTables: [params.structuredTable],
        structuredTableAdapters: [params.structuredTableAdapter],
        computedTables: [params.computedTable],
        tables: params.table ? [params.table] : [],
      });
    },
  };
}

export default {getRowAxisContextMenuItem: getUndoExecutionActionFunction};
